import Image from "next/image";
import { type ReactNode, use } from "react";

import { MessageLink } from "@/components/app/messages/message-link";
import DesoAvatar from "@/components/shared/deso-avatar";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import { UserContext } from "@/contexts/user-context";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

import { FollowUserListsButton } from "../app/lists/follow-user-lists-button";
import { TriggerLoggedInModalWindow, useIsLoggedIn } from "./is-logged-in";

type UserInfoRowProps = {
  user: PartialAccount;
  icon?: ReactNode;
  emoji?: string;
  extraContent?: ReactNode;
  customDescription?: ReactNode;
  iconClassName?: string;
  showUserActions?: boolean;
  mobileAccountCard?: boolean;
};

export function UserInfoRow({
  emoji,
  user,
  icon,
  iconClassName,
  extraContent,
  customDescription,
  showUserActions = true,
  mobileAccountCard = true,
}: UserInfoRowProps) {
  const { publicKey } = user;
  const followersTotalCount = user?.followerCounts?.totalFollowers ?? 0;
  const followingTotalCount = user?.followingCounts?.totalFollowing ?? 0;

  const { currentUserPublicKey } = use(UserContext);
  const isCurrentUser = currentUserPublicKey === publicKey;

  const isLoggedIn = useIsLoggedIn();

  if (!publicKey) {
    throw new Error("publicKey is required");
  }

  return (
    <div
      className="
        flex flex-col border-b border-border-light
        last:border-0
      "
    >
      <div
        className="
          flex flex-col items-start gap-4 p-4
          sm:flex-row sm:justify-between
        "
      >
        <div className="flex items-center gap-3">
          {emoji && (
            <div className="mr-1 text-muted">
              <Image
                src={emoji}
                width={30}
                height={30}
                alt="reaction"
                unoptimized
              />
            </div>
          )}
          {!!icon && (
            <div className={cn("mr-2 text-2xl text-muted", iconClassName)}>
              {icon}
            </div>
          )}

          <DesoAvatar {...{ account: user, size: 60 }} />

          <div className="flex flex-col gap-1">
            <div className="flex flex-col gap-1">
              <div className="flex flex-row items-center text-sm">
                <UserDisplayNameWithStatus
                  account={user}
                  className="font-sans text-sm"
                  mobileAccountCard={mobileAccountCard}
                  whaleSize={20}
                />
              </div>

              <div className="flex items-center gap-x-1">
                <div className="ml-0 inline-block text-sm text-muted">
                  {getUserName({ account: user })}
                </div>
              </div>
            </div>
            {customDescription || (
              <div className="flex items-start gap-4 text-sm text-muted">
                <div
                  className="
                    flex min-w-0 flex-col gap-0.5
                    sm:flex-row sm:items-center sm:gap-1
                  "
                >
                  <span className="font-medium text-muted-foreground">
                    {abbreviateNumber(followersTotalCount)}
                  </span>
                  <span className="font-normal text-muted">Followers</span>
                </div>
                <div
                  className="
                    flex min-w-0 flex-col gap-0.5
                    sm:flex-row sm:items-center sm:gap-1
                  "
                >
                  <span className="font-medium text-muted-foreground">
                    {abbreviateNumber(followingTotalCount)}
                  </span>
                  <span className="font-normal text-muted">Following</span>
                </div>
              </div>
            )}
          </div>
        </div>
        {showUserActions && !isCurrentUser && isLoggedIn && (
          <div
            className="
              ml-16 hidden items-center gap-4
              sm:ml-0 sm:flex
            "
          >
            <TriggerLoggedInModalWindow
              className="
                ml-16 hidden items-center gap-4
                sm:ml-0 sm:flex
              "
            >
              <FollowUserListsButton
                userLabel={getUserDisplayName(user)}
                userPublicKey={publicKey}
                variant="inline"
              />
              <MessageLink
                publicKey={publicKey}
                className="text-lg text-muted"
              />
            </TriggerLoggedInModalWindow>
          </div>
        )}
      </div>
      {extraContent}
    </div>
  );
}
